import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDraftCopies } from '../hooks/useDraftCopies';
import type { TaxFilingDocument } from '../types/taxFilings.types';
import {
  DRAFT_COPY_STATUS,
  DRAFT_COPY_STATUS_LABELS,
} from '../types/taxFilings.types';
import { RejectDraftDialog } from './RejectDraftDialog';
import {
  StatusBadge,
  TaxFilingDocumentRow,
  TaxFilingEmptyState,
  TaxFilingErrorState,
  TaxFilingLoadingState,
} from './TaxFilingDocumentRow';

const statusVariant = (
  status: TaxFilingDocument['status']
): 'secondary' | 'default' | 'destructive' | 'outline' => {
  switch (status) {
    case DRAFT_COPY_STATUS.CLIENT_VERIFIED:
      return 'default';
    case DRAFT_COPY_STATUS.CLIENT_REJECTED:
      return 'destructive';
    case DRAFT_COPY_STATUS.PENDING_CLIENT:
      return 'secondary';
    default:
      return 'outline';
  }
};

export function DraftCopiesTab() {
  const {
    documents,
    isLoading,
    error,
    getPreviewUrl,
    approve,
    reject,
  } = useDraftCopies();
  const [actionId, setActionId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<TaxFilingDocument | null>(null);
  const [isRejecting, setIsRejecting] = useState(false);

  const handleApprove = async (doc: TaxFilingDocument) => {
    setActionId(doc.id);
    try {
      await approve(doc.id);
    } finally {
      setActionId(null);
    }
  };

  const handleRejectConfirm = async (comment: string) => {
    if (!rejectTarget) return;
    setIsRejecting(true);
    try {
      await reject(rejectTarget.id, comment || undefined);
      setRejectTarget(null);
    } finally {
      setIsRejecting(false);
    }
  };

  if (isLoading) return <TaxFilingLoadingState />;
  if (error) return <TaxFilingErrorState message={error} />;
  if (documents.length === 0) {
    return (
      <TaxFilingEmptyState
        title="No draft copies yet"
        description="Draft returns from your preparer will appear here for review."
      />
    );
  }

  return (
    <>
      <div className="space-y-2">
        {documents.map((doc) => (
          <TaxFilingDocumentRow
            key={doc.id}
            document={doc}
            getPreviewUrl={getPreviewUrl}
            badge={
              doc.status ? (
                <StatusBadge
                  label={DRAFT_COPY_STATUS_LABELS[doc.status]}
                  variant={statusVariant(doc.status)}
                />
              ) : undefined
            }
            meta={
              doc.rejectionComment ? (
                <span className="block mt-0.5 text-destructive">
                  Comment: {doc.rejectionComment}
                </span>
              ) : null
            }
            actions={
              doc.status === DRAFT_COPY_STATUS.PENDING_CLIENT ? (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        className="h-8 gap-1.5"
                        onClick={() => handleApprove(doc)}
                        disabled={actionId === doc.id}
                      >
                        {actionId === doc.id ? (
                          <Spinner size="sm" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                        Approve
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-medium">Approve</p>
                      <p className="text-muted-foreground text-xs">
                        Mark as verified by client
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-8 gap-1.5"
                        onClick={() => setRejectTarget(doc)}
                        disabled={actionId === doc.id}
                      >
                        <X className="h-4 w-4" />
                        Reject
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-medium">Reject</p>
                      <p className="text-muted-foreground text-xs">
                        Mark as rejected with optional comment
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </>
              ) : undefined
            }
          />
        ))}
      </div>

      <RejectDraftDialog
        open={rejectTarget != null}
        documentName={rejectTarget?.name}
        isSubmitting={isRejecting}
        onOpenChange={(open) => {
          if (!open) setRejectTarget(null);
        }}
        onConfirm={handleRejectConfirm}
      />
    </>
  );
}
